import React, { useState, useEffect } from 'react';

/**
 * Reusable slug field component with auto-suggest and validation
 * @param {string} value - Current slug value
 * @param {function} onChange - Change handler
 * @param {string} sourceValue - Source value to auto-generate slug from (e.g., title)
 * @param {boolean} isEdit - Whether this is an edit form
 * @param {string} originalSlug - Original slug value (for edit mode warning)
 */
export const SlugField = ({ 
  value = '', 
  onChange, 
  sourceValue = '', 
  isEdit = false,
  originalSlug = '',
  label = 'Slug',
  required = true
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [manualEdit, setManualEdit] = useState(false);

  // Auto-generate slug from source value (title/name)
  useEffect(() => {
    if (!isEdit && !manualEdit && sourceValue) {
      const generatedSlug = generateSlug(sourceValue);
      if (generatedSlug && (!value || value === '')) {
        onChange(generatedSlug);
      }
    }
  }, [sourceValue, isEdit, manualEdit, value, onChange]);

  // Show warning if slug changed in edit mode
  useEffect(() => {
    if (isEdit && originalSlug && value !== originalSlug) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  }, [value, originalSlug, isEdit]);

  const generateSlug = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleChange = (e) => {
    setManualEdit(true);
    onChange(e.target.value);
  };

  const handleRegenerate = () => {
    if (sourceValue) {
      const generatedSlug = generateSlug(sourceValue);
      onChange(generatedSlug);
      setManualEdit(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-[#111827] mb-2">
        {label} {required && '*'}
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          required={required}
          value={value}
          onChange={handleChange}
          className="flex-1 px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
          placeholder="url-friendly-slug"
        />
        {sourceValue && (
          <button
            type="button"
            onClick={handleRegenerate}
            className="px-3 py-2 text-sm text-[#1E40AF] hover:bg-[#EEF2FF] rounded-lg transition-colors"
            title="Regenerate from title"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        )}
      </div>
      <p className="text-xs text-[#6B7280] mt-1">URL-friendly identifier. Auto-generated from title.</p>
      {showWarning && (
        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <strong>Warning:</strong> Changing the slug will break existing links to this page.
          </p>
        </div>
      )}
    </div>
  );
};

export default SlugField;
